import { type ReactNode, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ReportScaffold } from "@/features/reports/components/report-scaffold";
import { ReportTable } from "@/features/reports/components/report-table";
import { exportReportXlsx, fetchReport, type ReportParams } from "@/features/reports/api";
import { REPORT_CATALOG } from "@/features/reports/report-catalog";
import { formatCents } from "@/lib/utils";
import type { ReportColumn, ReportDef, ReportResult, SortState } from "@/features/reports/types";
import { ApiError } from "@/lib/api";

function initialFilters(def: ReportDef): Record<string, string> {
  const values: Record<string, string> = {};
  for (const f of def.filters ?? []) values[f.param] = f.default;
  return values;
}

/** Keyed wrapper so navigating between two `/reports/:reportKey` entries while
 * this element stays mounted remounts the inner page — resetting its filter/sort
 * state instead of carrying one report's state onto another. */
export default function ReportPage() {
  const { reportKey } = useParams();
  return <ReportPageInner key={reportKey ?? "none"} />;
}

function ReportPageInner() {
  const { reportKey } = useParams();
  const def = reportKey ? REPORT_CATALOG[reportKey] : undefined;

  const [filters, setFilters] = useState<Record<string, string>>(() =>
    def ? initialFilters(def) : {},
  );
  const [sort, setSort] = useState<SortState | undefined>();
  const [result, setResult] = useState<ReportResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const params: ReportParams = useMemo(
    () => ({ ...filters, ...(sort ? { sort: sort.sort, direction: sort.direction } : {}) }),
    [filters, sort],
  );

  useEffect(() => {
    if (!def) return;
    let active = true;
    setLoading(true);
    setError(null);
    fetchReport(def.key, params)
      .then((r) => active && setResult(r))
      .catch((e) =>
        active && setError(e instanceof ApiError ? e.message : "Couldn't load the report."),
      )
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [def, params]);

  if (!def) {
    return (
      <div className="flex flex-col items-center gap-2 py-24 text-center">
        <p className="text-lg font-semibold">Report not found</p>
        <Link to="/reports" className="text-primary text-sm underline">
          Back to all reports
        </Link>
      </div>
    );
  }

  function toggleSort(sortKey: string) {
    setSort((prev) =>
      prev?.sort === sortKey
        ? { sort: sortKey, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { sort: sortKey, direction: "asc" },
    );
  }

  async function handleExport() {
    if (!def) return;
    setExporting(true);
    try {
      await exportReportXlsx(def.key, params);
    } catch {
      toast.error("Couldn't export the report.");
    } finally {
      setExporting(false);
    }
  }

  const data = result ?? { rows: [], meta: {} };

  return (
    <ReportScaffold
      title={def.title}
      description={def.description}
      note={def.note}
      filters={def.filters}
      filterValues={filters}
      onFilterChange={(param, value) => setFilters((f) => ({ ...f, [param]: value }))}
      onExport={handleExport}
      exporting={exporting}
      loading={loading}
      error={error}
      headerExtra={<HeaderExtra def={def} meta={data.meta} />}
    >
      <ReportBody def={def} data={data} sort={sort} onSort={toggleSort} />
    </ReportScaffold>
  );
}

function HeaderExtra({ def, meta }: { def: ReportDef; meta: Record<string, unknown> }) {
  if (def.key === "caf-monitoring") {
    const url = meta.irs_processing_url as string | undefined;
    const lastCheck = meta.last_check as string | undefined;
    return (
      <div className="text-muted-foreground flex flex-wrap gap-4 text-xs">
        {lastCheck && <span>Last check: {lastCheck}</span>}
        {url && (
          <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary underline">
            IRS CAF processing times
          </a>
        )}
      </div>
    );
  }
  if (def.key === "outstanding-balance" || def.key === "delinquent-passports") {
    const threshold = meta.passport_threshold_cents as number | undefined;
    if (threshold !== undefined) {
      return (
        <p className="text-muted-foreground text-xs">
          Passport threshold: {formatCents(threshold)}
        </p>
      );
    }
  }
  return null;
}

function ReportBody({
  def,
  data,
  sort,
  onSort,
}: {
  def: ReportDef;
  data: ReportResult;
  sort?: SortState;
  onSort: (k: string) => void;
}) {
  switch (def.layout) {
    case "unfiled":
      return <UnfiledBody data={data} sort={sort} onSort={onSort} />;
    case "accounts-receivable":
      return <AccountsReceivableBody data={data} sort={sort} onSort={onSort} />;
    case "delinquent-passports":
      return <DelinquentPassportsBody data={data} />;
    case "transcript-monitoring":
      return <TranscriptMonitoringBody def={def} data={data} sort={sort} onSort={onSort} />;
    case "fta-finder":
      return <FtaFinderBody def={def} data={data} sort={sort} onSort={onSort} />;
    default:
      return (
        <ReportTable
          columns={def.columns ?? []}
          rows={data.rows}
          sort={sort}
          onSort={onSort}
          rowClassName={(row) => (row.danger ? "bg-destructive/10" : undefined)}
        />
      );
  }
}

const T = "text" as const;

function UnfiledBody({
  data,
  sort,
  onSort,
}: {
  data: ReportResult;
  sort?: SortState;
  onSort: (k: string) => void;
}) {
  const years = (data.meta.years as number[] | undefined) ?? [];
  const columns: ReportColumn[] = [
    { key: "client", label: "Client", kind: T, sortKey: "client" },
    { key: "form", label: "Form", kind: T, sortKey: "form" },
    ...years.map((y) => ({ key: `y_${y}`, label: String(y), kind: T })),
    { key: "primary_practitioner", label: "Primary Practitioner", kind: T },
  ];
  const rows = data.rows.map((row) => {
    const flat: Record<string, unknown> = {
      client: row.client,
      form: row.form,
      primary_practitioner: row.primary_practitioner,
    };
    for (const cell of (row.years as Array<{ year: number; value: string }>) ?? []) {
      flat[`y_${cell.year}`] = cell.value;
    }
    return flat;
  });
  return <ReportTable columns={columns} rows={rows} sort={sort} onSort={onSort} />;
}

const AR_COLUMNS: ReportColumn[] = [
  { key: "taxpayer", label: "Taxpayer", kind: "text", sortKey: "taxpayer" },
  { key: "current", label: "Current", kind: "money", align: "right" },
  { key: "d1_30", label: "1-30", kind: "money", align: "right" },
  { key: "d31_60", label: "31-60", kind: "money", align: "right" },
  { key: "d61_90", label: "61-90", kind: "money", align: "right" },
  { key: "d90_plus", label: ">90", kind: "money", align: "right" },
  { key: "total_cents", label: "Total", kind: "money", sortKey: "total", align: "right" },
];

function AccountsReceivableBody({
  data,
  sort,
  onSort,
}: {
  data: ReportResult;
  sort?: SortState;
  onSort: (k: string) => void;
}) {
  const totals = data.meta.totals as Record<string, unknown> | undefined;
  const rows = totals ? [...data.rows, totals] : data.rows;
  return (
    <ReportTable
      columns={AR_COLUMNS}
      rows={rows}
      sort={sort}
      onSort={onSort}
      rowClassName={(row) => (row.taxpayer === "Total" ? "bg-muted/40 font-semibold" : undefined)}
    />
  );
}

const CERTIFIED_COLUMNS: ReportColumn[] = [
  { key: "client", label: "Client", kind: "text" },
  { key: "tax_years", label: "Tax Year(s)", kind: "text" },
  { key: "transcript_date", label: "Transcript Date", kind: "date" },
  { key: "tax_due_cents", label: "Tax Due", kind: "money", align: "right" },
  { key: "primary_practitioner", label: "Primary Practitioner", kind: "text" },
];
const POSSIBLE_COLUMNS: ReportColumn[] = [
  { key: "client", label: "Client", kind: "text" },
  { key: "tax_due_cents", label: "Tax Due", kind: "money", align: "right" },
  { key: "primary_practitioner", label: "Primary Practitioner", kind: "text" },
];

function SectionHeading({ children }: { children: ReactNode }) {
  return <h3 className="text-muted-foreground bg-muted/30 px-4 py-2 text-sm font-semibold">{children}</h3>;
}

function DelinquentPassportsBody({ data }: { data: ReportResult }) {
  const possibles = (data.meta.possibles as Array<Record<string, unknown>>) ?? [];
  return (
    <div>
      <SectionHeading>Certified seriously delinquent</SectionHeading>
      <ReportTable columns={CERTIFIED_COLUMNS} rows={data.rows} emptyMessage="No certified clients." />
      <SectionHeading>In danger of being reported</SectionHeading>
      <ReportTable columns={POSSIBLE_COLUMNS} rows={possibles} emptyMessage="No clients over threshold." />
    </div>
  );
}

const FTA_ERROR_COLUMNS: ReportColumn[] = [
  { key: "client", label: "Client", kind: "text" },
  { key: "error", label: "Error", kind: "text" },
  { key: "pract_name", label: "Primary Practitioner", kind: "text" },
];

function FtaFinderBody({
  def,
  data,
  sort,
  onSort,
}: {
  def: ReportDef;
  data: ReportResult;
  sort?: SortState;
  onSort: (k: string) => void;
}) {
  const errors = (data.meta.errors as Array<Record<string, unknown>>) ?? [];
  return (
    <div>
      <ReportTable
        columns={def.columns ?? []}
        rows={data.rows}
        sort={sort}
        onSort={onSort}
        emptyMessage="No first-time-abatement candidates."
      />
      {errors.length > 0 && (
        <>
          <SectionHeading>Unable to properly check the following clients:</SectionHeading>
          <ReportTable columns={FTA_ERROR_COLUMNS} rows={errors} />
        </>
      )}
    </div>
  );
}

const CAF_OVERVIEW_COLUMNS: ReportColumn[] = [
  { key: "client", label: "Client", kind: "text" },
  { key: "form", label: "Form", kind: "text" },
  { key: "years", label: "Years", kind: "text" },
  { key: "last_check", label: "Last Check", kind: "date" },
  { key: "status", label: "Status", kind: "text" },
];
const MONITOR_OVERVIEW_COLUMNS: ReportColumn[] = [
  { key: "client", label: "Client", kind: "text" },
  { key: "form", label: "Form", kind: "text" },
  { key: "years", label: "Years", kind: "text" },
  { key: "last_check", label: "Last Check", kind: "date" },
  { key: "alerts", label: "Alerts", kind: "int", align: "right" },
  { key: "last_result", label: "Notes", kind: "text" },
];

function TranscriptMonitoringBody({
  def,
  data,
  sort,
  onSort,
}: {
  def: ReportDef;
  data: ReportResult;
  sort?: SortState;
  onSort: (k: string) => void;
}) {
  const overview = (data.meta.status_overview as
    | { caf: Array<Record<string, unknown>>; monitors: Array<Record<string, unknown>> }
    | undefined) ?? { caf: [], monitors: [] };
  return (
    <Tabs defaultValue="monitors">
      <div className="px-4 pt-3">
        <TabsList>
          <TabsTrigger value="monitors">Monitors</TabsTrigger>
          <TabsTrigger value="overview">Status overview</TabsTrigger>
        </TabsList>
      </div>
      <TabsContent value="monitors" className="mt-2">
        <ReportTable columns={def.columns ?? []} rows={data.rows} sort={sort} onSort={onSort} />
      </TabsContent>
      <TabsContent value="overview" className="mt-2">
        <SectionHeading>CAF checks</SectionHeading>
        <ReportTable columns={CAF_OVERVIEW_COLUMNS} rows={overview.caf} emptyMessage="No CAF checks." />
        <SectionHeading>Transcript monitors</SectionHeading>
        <ReportTable
          columns={MONITOR_OVERVIEW_COLUMNS}
          rows={overview.monitors}
          emptyMessage="No monitors."
        />
      </TabsContent>
    </Tabs>
  );
}
